// Helpers to classify the errors rejected by BackendService.callApi
// (HttpErrorResponse, rxjs TimeoutError from the TimeoutInterceptor, etc.)

const transientHttpStatuses: ReadonlyArray<number> = [0, 408, 429, 500, 502, 503, 504];

/** HTTP status of a rejected backend call (0 means network error), undefined if it is not an HTTP error. */
export function getHttpErrorStatus(error: any): number | undefined {
  return typeof error?.status === "number" ? error.status : undefined;
}

/** Network errors, client side timeouts, rate limits and gateway/server errors that are worth retrying. */
export function isTransientHttpError(error: any): boolean {
  if (error?.name === "TimeoutError") {
    return true;
  }

  const status: number | undefined = getHttpErrorStatus(error);
  return status !== undefined && transientHttpStatuses.includes(status);
}

/** The server deterministically rejected the request, so repeating it unchanged would fail again. */
export function isPermanentHttpClientError(error: any): boolean {
  const status: number | undefined = getHttpErrorStatus(error);
  return status !== undefined && status >= 400 && status < 500 && !transientHttpStatuses.includes(status) && status !== 401;
}

/**
 * Stable, machine readable error code from an error response body
 * (e.g. { code: "UPLOAD_SIZE_MISMATCH" } or { error: "UPLOAD_SIZE_MISMATCH" }).
 * Nest's generic texts like "Bad Request" are not codes, so they are ignored.
 */
export function getServerErrorCode(error: any): string | undefined {
  const body: any = error?.error;
  if (body == null || typeof body !== "object") {
    return undefined;
  }

  const candidates: Array<unknown> = [body.code, body.errorCode, body.error?.code, body.error];
  return candidates.find(
    (candidate: unknown): candidate is string => typeof candidate === "string" && /^[A-Z][A-Z0-9_]+$/.test(candidate)
  );
}
